import { NoSymbolIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { createApiKey, deleteApiKey, getApiKeys, getApiKeyScopes, revokeApiKey } from 'services/apis/admin'
import { filterUserRoles } from 'services/apis/user'
import { Button, PlainTable } from 'stories/components'
import { confirm, formatTime } from 'utils'

import { ApiKeyCreatedModal } from './ApiKeyCreatedModal'
import { CreateApiKeyModal } from './CreateApiKeyModal'
import { describeScopes, IApiKey, IApiKeyScope, ICreatedApiKey, STATUS_CLASS, STATUS_LABELS } from './type'

/**
 * Expiry is a whole day, stored as end-of-day UTC. Render the stored date itself rather than
 * passing it through a timezone-aware formatter, which would show the following day to anyone
 * whose timezone is ahead of UTC.
 */
const formatExpiryDay = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return y && m && d ? `${m}/${d}/${y}` : ''
}

/**
 * Admin Tools > API Keys: issue, review and revoke the external API credentials this company
 * hands to integration partners.
 *
 * Keys replaced a single static value in the server environment, which could not be listed,
 * attributed or revoked without a redeploy. The screen therefore leads with the things that value
 * could never answer: who issued each key, when it was last used, and whether it is still live.
 */
export const ApiKeys = () => {
  const [action, setAction] = useState('')
  const [keys, setKeys] = useState<IApiKey[]>([])
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<ICreatedApiKey | null>(null)
  // Only for turning a stored scope value into its label; a key with no match still shows the raw
  // value, so a scope the catalogue has not heard of is visible rather than hidden.
  const [scopeCatalogue, setScopeCatalogue] = useState<IApiKeyScope[]>([])
  // Resolved names for the accounts keys run as, keyed by user id as a string.
  const [runAsNames, setRunAsNames] = useState<Record<string, string>>({})

  /**
   * The key list serves `runAsUserId` and no name, so the screen looks the accounts up itself.
   * `filterUserRoles` takes an id as its query and needs no permission beyond being signed in,
   * unlike the user list, which requires the User permission this screen does not grant. One call
   * per distinct account, which in practice is one or two.
   */
  const resolveRunAsNames = async (list: IApiKey[]) => {
    const ids = Array.from(new Set(list.map((item) => item.runAsUserId).filter((id): id is number => !!id)))
    if (!ids.length) return
    const found = await Promise.all(
      ids.map((id) =>
        filterUserRoles(String(id))
          .then((data: Record<string, string>) => [String(id), data?.[String(id)] || ''])
          .catch(() => [String(id), '']),
      ),
    )
    setRunAsNames(Object.fromEntries(found.filter(([, label]) => label)))
  }

  const fetchData = async () => {
    setAction('get')
    try {
      getApiKeyScopes()
        .then((list: IApiKeyScope[]) => setScopeCatalogue(Array.isArray(list) ? list : []))
        .catch(() => setScopeCatalogue([]))
      const data = await getApiKeys()
      const list: IApiKey[] = Array.isArray(data) ? data : []
      setKeys(list)
      await resolveRunAsNames(list)
    } finally {
      setAction('')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const onCloseCreate = async (payload: Record<string, any> | null) => {
    if (!payload) {
      setCreating(false)
      return
    }
    setAction('create')
    try {
      const result = await createApiKey(payload)
      setCreating(false)
      // Hand straight to the reveal: this is the only moment the raw key exists.
      if (result?.key) setCreated(result)
      await fetchData()
    } finally {
      setAction('')
    }
  }

  const onRevoke = async (item: IApiKey) => {
    const result = await confirm(
      `Revoke "${item.name}"? Anything using this key stops working immediately. This cannot be undone.`,
    )
    if (!result) return
    setAction('revoke')
    try {
      await revokeApiKey(item.id)
      await fetchData()
    } finally {
      setAction('')
    }
  }

  const onDelete = async (item: IApiKey) => {
    const result = await confirm(`Delete the record for "${item.name}"? The audit trail for this key is removed too.`)
    if (!result) return
    setAction('delete')
    try {
      await deleteApiKey(item.id)
      await fetchData()
    } finally {
      setAction('')
    }
  }

  const tableData = keys.map((item) => [
    <div>
      <p className="font-semibold">{item.name}</p>
      {item.description ? <p className="text-xs text-gray-500 whitespace-normal">{item.description}</p> : null}
    </div>,
    <span className="font-mono text-xs">{item.keyPrefix}…</span>,
    <span className="text-xs whitespace-normal">{describeScopes(item.scopes, scopeCatalogue)}</span>,
    <span className="text-xs whitespace-normal">
      {item.runAsUserId ? (
        runAsNames[String(item.runAsUserId)] || `User ${item.runAsUserId}`
      ) : (
        <span className="text-gray-400">Integration endpoints only</span>
      )}
    </span>,
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASS[item.status]}`}>
      {STATUS_LABELS[item.status]}
    </span>,
    <span className="text-xs">{item.lastUsedAt ? formatTime(item.lastUsedAt) : <span className="text-gray-400">Never used</span>}</span>,
    <span className="text-xs">
      {item.expiresAt ? formatExpiryDay(item.expiresAt) : <span className="text-gray-400">—</span>}
    </span>,
    <div className="text-xs">
      <p>{item.createdBy || '—'}</p>
      <p className="text-gray-400">{formatTime(item.createdAt)}</p>
    </div>,
    <div className="flex items-center gap-2">
      {item.status === 'active' && (
        <span className="btn-icon" onClick={() => onRevoke(item)} title="Revoke">
          <NoSymbolIcon className="w-4 h-4 text-red-500" />
        </span>
      )}
      <span className="btn-icon" onClick={() => onDelete(item)} title="Delete record">
        <TrashIcon className="w-4 h-4 text-gray-400" />
      </span>
    </div>,
  ])

  return (
    <div className="relative">
      <LayoutLoading show={action !== ''} />
      <div className="flex justify-between items-center pb-5">
        <h1 className="text-2xl font-bold">API Keys</h1>
        <Button onClick={() => setCreating(true)}>Create Key</Button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Credentials for partners calling this company&apos;s external API. A key is sent as the
        <span className="font-mono"> Authorization </span>
        header with no <span className="font-mono">Bearer</span> prefix. Each key grants only the access chosen when it
        was issued, shown in the Access column. A key also reaches only the endpoints built for integrations unless it
        was given an account to run as, which is chosen when the key is created and cannot be changed afterwards. Keys
        are shown once at creation and stored only as a hash, so revoke and reissue rather than trying to look one up.
      </p>

      <div className="overflow-auto sm:rounded-lg">
        <PlainTable
          thClass="!px-4 !py-2"
          tdClass="px-4 py-2 align-top"
          header={['Name', 'Key', 'Access', 'Runs as', 'Status', 'Last used', 'Expires', 'Created', 'Action']}
          data={tableData}
        />
      </div>

      {creating && <CreateApiKeyModal onClose={onCloseCreate} />}
      {created && (
        <ApiKeyCreatedModal name={created.record?.name || ''} apiKey={created.key} onClose={() => setCreated(null)} />
      )}
    </div>
  )
}
